import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
  Info,
  Loader2,
  Plus,
  Upload as UploadIcon,
} from 'lucide-react'
import { Page } from '@/components/Page'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { DataTable } from '@/components/DataTable'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Toolbar } from '@/components/Toolbar'
import { Combobox, type ComboboxOption } from '@/components/Combobox'
import { Modal } from '@/components/Modal'
import { FormField } from '@/components/FormField'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { useToast } from '@/hooks/useToast'
import { cn } from '@/lib/cn'
import { ApiError } from '@/lib/api'
import { formatCurrencyPrecise } from '@/lib/locale'
import { useCoa } from '@/features/coa/queries'
import {
  useAutoMap,
  useBalanceSheet,
  useImportTb,
  useIncludeSkipped,
  useMappings,
  useSaveMappings,
  useTbDiagnostics,
  useUploadTb,
  useValidateTb,
} from '@/features/tbImport/queries'
import type {
  BalanceSheetPeriod,
  MappingPatch,
  TbDiagnosticsBatch,
  TbMappingRow,
  TbPreviewRow,
  TbSkippedRow,
  TbUploadResponse,
  TbValidationResponse,
} from '@/features/tbImport/types'
import type { TaxonomyItem } from '@/features/coa/types'

type Step = 1 | 2 | 3 | 4 | 5

const STEPS: { n: Step; label: string }[] = [
  { n: 1, label: 'Upload' },
  { n: 2, label: 'Map accounts' },
  { n: 3, label: 'Validate' },
  { n: 4, label: 'Import' },
  { n: 5, label: 'Verify' },
]

function errorMessage(err: unknown, fallback = 'Something went wrong.'): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

function fmtMoney(v: number | string | null | undefined): string {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? 0))
  if (!isFinite(n)) return formatCurrencyPrecise(0)
  return formatCurrencyPrecise(n)
}

/**
 * Convert a 0-based column index into the spreadsheet letter the user
 * sees (0 → A, 1 → B, 25 → Z, 26 → AA …). Used in the "column
 * detection" diagnostic so the user reads "column B" instead of
 * "index 1".
 */
function colLetter(idx: number | undefined): string {
  if (idx === undefined || idx < 0) return '-'
  let n = idx
  let s = ''
  while (true) {
    s = String.fromCharCode(65 + (n % 26)) + s
    n = Math.floor(n / 26) - 1
    if (n < 0) break
  }
  return s
}

function taxonomyOption(t: TaxonomyItem): ComboboxOption<string> {
  // Each taxonomy row is distinct (id-backed), but we encode into a single
  // string value so Combobox can round-trip it without an extra Map lookup.
  // Format: id|account_code|statement_type|main_category|subcategory
  const value = `${t.id}|${t.account_code}|${t.statement_type}|${t.main_category}|${t.subcategory_name}`
  return {
    value,
    label: `${t.account_code} - ${t.subcategory_name}`,
    description: `${t.main_category} · ${t.statement_type}`,
  }
}

function parseValue(value: string | null):
  | {
      id: string
      code: string
      stmt: string
      main: string
      sub: string
    }
  | null {
  if (!value) return null
  const [id, code, stmt, main, sub] = value.split('|')
  if (!code) return null
  return { id, code, stmt, main, sub }
}

export function TbImportPage() {
  const { toast } = useToast()
  const [step, setStep] = useState<Step>(1)
  const [asOfDate, setAsOfDate] = useState('')
  const [batchId, setBatchId] = useState<string | null>(null)
  const [upload, setUpload] = useState<TbUploadResponse | null>(null)

  // Step-2 local state - `value` keyed by row id holds the encoded combobox value
  const [mapDrafts, setMapDrafts] = useState<Record<number, string>>({})
  const [highlightUnmapped, setHighlightUnmapped] = useState(false)
  const [unmappedAlert, setUnmappedAlert] = useState<string[] | null>(null)

  // Validation / import results
  const [validation, setValidation] = useState<TbValidationResponse | null>(null)
  const [importedSummary, setImportedSummary] = useState<{ rows: number; asOf: string } | null>(
    null,
  )
  const [verify, setVerify] = useState<BalanceSheetPeriod | null>(null)
  const [importErr, setImportErr] = useState<string | null>(null)

  // Duplicate-as-of-date confirmation - the import endpoint returns 409 with
  // a list of prior imported batches for the same date; we show that list in
  // a modal and let the user retry with confirmDuplicate=true.
  const [duplicateConflict, setDuplicateConflict] = useState<TbDiagnosticsBatch[] | null>(null)

  const coa = useCoa()
  const mappingsQuery = useMappings(step === 2 ? batchId : null)
  const uploadMut = useUploadTb()
  const autoMapMut = useAutoMap()
  const saveMut = useSaveMappings()
  const validateMut = useValidateTb()
  const importMut = useImportTb()
  const includeSkippedMut = useIncludeSkipped()
  const bsMut = useBalanceSheet()

  /**
   * Re-include rows the parser had filtered out. Called from the
   * expandable Skipped-rows panel on Step 1. Updates the local upload
   * state so the summary banner / preview / "show all" toggle pick up
   * the new totals without a full re-upload.
   */
  const onIncludeSkipped = async (selected: TbSkippedRow[]) => {
    if (!batchId || selected.length === 0) return
    try {
      const result = await includeSkippedMut.mutateAsync({
        batchId,
        rows: selected.map((r) => ({
          externalAccountCode: r.externalAccountCode,
          externalAccountName: r.externalAccountName,
          debit: r.debit,
          credit: r.credit,
        })),
      })
      const includedSet = new Set(selected.map((r) => r.rowIndex))
      setUpload((prev) =>
        prev
          ? {
              ...prev,
              totalRows: result.totalRows,
              totalDebits: result.totalDebits,
              totalCredits: result.totalCredits,
              isBalanced: result.isBalanced,
              skippedRows: (prev.skippedRows ?? []).filter(
                (r) => !includedSet.has(r.rowIndex),
              ),
              skippedRowCount: Math.max(
                0,
                (prev.skippedRowCount ?? 0) - result.insertedCount,
              ),
            }
          : prev,
      )
      toast({
        title: `Added ${result.insertedCount} row${result.insertedCount === 1 ? '' : 's'}`,
        description: `Total now ${result.totalRows} accounts.`,
      })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Could not include rows',
        description: errorMessage(err),
      })
    }
  }

  const taxonomyOptions = useMemo<ComboboxOption<string>[]>(
    () => (coa.data ?? []).map(taxonomyOption),
    [coa.data],
  )
  const taxonomyByCode = useMemo(() => {
    const m = new Map<string, TaxonomyItem>()
    for (const t of coa.data ?? []) if (!m.has(t.account_code)) m.set(t.account_code, t)
    return m
  }, [coa.data])

  // Seed mapDrafts from the server response whenever it refreshes
  useEffect(() => {
    if (!mappingsQuery.data) return
    const next: Record<number, string> = {}
    for (const row of mappingsQuery.data.rows) {
      if (row.mapped_account_code) {
        const t = taxonomyByCode.get(row.mapped_account_code)
        if (t) next[row.id] = taxonomyOption(t).value
      }
    }
    setMapDrafts(next)
    setHighlightUnmapped(false)
  }, [mappingsQuery.data, taxonomyByCode])

  const resetWizard = () => {
    setStep(1)
    setBatchId(null)
    setUpload(null)
    setAsOfDate('')
    setMapDrafts({})
    setValidation(null)
    setImportedSummary(null)
    setVerify(null)
    setImportErr(null)
    setHighlightUnmapped(false)
    setDuplicateConflict(null)
  }

  /**
   * Actually run the import - shared between the initial click on "Import"
   * and the "Import anyway" branch of the duplicate-date confirmation modal.
   * On a 409 the server returns the list of prior batches; we stash those
   * into `duplicateConflict` to show the modal and stay on step 3.
   */
  const runImport = async (confirmDuplicate: boolean) => {
    if (!batchId) return
    setStep(4)
    setImportErr(null)
    setImportedSummary(null)
    try {
      const result = await importMut.mutateAsync({ batchId, confirmDuplicate })
      setImportedSummary({ rows: result.importedRows, asOf: result.asOfDate })
      try {
        const bs = await bsMut.mutateAsync(result.asOfDate)
        setVerify(bs.periods[0] ?? null)
      } catch {
        /* verify failure is non-fatal */
      }
      setStep(5)
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.status === 409 &&
        err.body &&
        typeof err.body === 'object' &&
        (err.body as { code?: string }).code === 'DUPLICATE_AS_OF_DATE'
      ) {
        const body = err.body as { existingBatches?: TbDiagnosticsBatch[] }
        setDuplicateConflict(body.existingBatches ?? [])
        setStep(3)
        return
      }
      setImportErr(errorMessage(err))
    }
  }

  return (
    <Page
      title="Trial balance import"
      description="Upload an external trial balance, map accounts, validate, and post."
      width="wide"
    >
      <StepIndicator current={step} />

      {step === 1 ? (
        <StepUpload
          asOfDate={asOfDate}
          onAsOfDate={setAsOfDate}
          onUploaded={(resp) => {
            setUpload(resp)
            setBatchId(resp.batchId)
          }}
          upload={upload}
          uploading={uploadMut.isPending}
          batchId={batchId}
          onIncludeSkipped={onIncludeSkipped}
          includingSkipped={includeSkippedMut.isPending}
          runUpload={async (file) => {
            if (!asOfDate) {
              toast({
                variant: 'destructive',
                title: 'Date required',
                description: 'Pick the Trial Balance "as of" date first.',
              })
              return
            }
            try {
              const resp = await uploadMut.mutateAsync({ file, asOfDate })
              setUpload(resp)
              setBatchId(resp.batchId)
            } catch (err) {
              toast({
                variant: 'destructive',
                title: 'Upload failed',
                description: errorMessage(err),
              })
            }
          }}
          onNext={() => setStep(2)}
        />
      ) : null}

      {step === 2 ? (
        <StepMap
          rows={mappingsQuery.data?.rows ?? []}
          loading={mappingsQuery.isLoading || coa.isLoading}
          options={taxonomyOptions}
          drafts={mapDrafts}
          highlightUnmapped={highlightUnmapped}
          onChange={(rowId, value) => setMapDrafts((prev) => ({ ...prev, [rowId]: value ?? '' }))}
          onAutoMap={async () => {
            if (!batchId) return
            try {
              const r = await autoMapMut.mutateAsync(batchId)
              toast({ title: `Mapped ${r.mappedCount} of ${r.totalRows} accounts.` })
              await mappingsQuery.refetch()
            } catch (err) {
              toast({
                variant: 'destructive',
                title: 'Auto-map failed',
                description: errorMessage(err),
              })
            }
          }}
          autoMapping={autoMapMut.isPending}
          onBack={() => setStep(1)}
          onSave={async () => {
            if (!batchId) return
            const rows = (mappingsQuery.data?.rows ?? []).map<MappingPatch>((r) => {
              const draft = mapDrafts[r.id]
              const parsed = parseValue(draft ?? null)
              return {
                id: r.id,
                external_account_code: r.external_account_code,
                external_account_name: r.external_account_name,
                mapped_account_code: parsed?.code ?? null,
                mapped_statement_type: parsed?.stmt ?? null,
                mapped_main_category: parsed?.main ?? null,
                mapped_subcategory: parsed?.sub ?? null,
                mapping_status: parsed ? 'mapped' : 'unmapped',
              }
            })
            const unmappedNames = rows
              .filter((r) => r.mapping_status === 'unmapped')
              .map((r) => r.external_account_name || r.external_account_code || 'Unknown')
            if (unmappedNames.length > 0) {
              setHighlightUnmapped(true)
              setUnmappedAlert(unmappedNames)
              return
            }
            try {
              await saveMut.mutateAsync({ batchId, rows })
              const result = await validateMut.mutateAsync(batchId)
              setValidation(result)
              setStep(3)
            } catch (err) {
              toast({
                variant: 'destructive',
                title: 'Save failed',
                description: errorMessage(err),
              })
            }
          }}
          saving={saveMut.isPending || validateMut.isPending}
        />
      ) : null}

      {step === 3 ? (
        <StepValidate
          result={validation}
          onBack={() => setStep(2)}
          onImport={() => void runImport(false)}
          importing={importMut.isPending}
        />
      ) : null}

      {step === 4 ? <StepImporting error={importErr} onBack={() => setStep(3)} /> : null}

      {step === 5 ? (
        <StepVerify
          batchId={batchId}
          summary={importedSummary}
          period={verify}
          onBackToMap={() => setStep(2)}
          onReset={resetWizard}
        />
      ) : null}

      <Modal
        open={duplicateConflict !== null}
        onOpenChange={(v) => !v && setDuplicateConflict(null)}
        title={`Another TB for ${asOfDate || 'this date'} is already imported`}
        footer={
          <Toolbar align="end">
            <Button variant="secondary" onClick={() => setDuplicateConflict(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setDuplicateConflict(null)
                void runImport(true)
              }}
            >
              Import anyway
            </Button>
          </Toolbar>
        }
      >
        <p className="text-sm">
          Importing now will <strong>stack</strong> this batch&rsquo;s entries on top of the existing
          ones in the ledger - nothing deletes the earlier rows automatically. The Verify balance
          check will almost certainly come back &ldquo;NO&rdquo; until you remove the duplicate.
        </p>
        {duplicateConflict && duplicateConflict.length > 0 ? (
          <ul className="mt-3 space-y-1 text-sm">
            {duplicateConflict.map((b) => (
              <li key={b.id} className="rounded-md border bg-muted/40 px-3 py-2">
                <div className="font-medium">{b.filename || `Batch ${b.id}`}</div>
                <div className="text-xs text-muted-foreground">
                  Imported {b.importedRows ?? 0} rows · {new Date(b.createdAt).toLocaleString()}
                </div>
              </li>
            ))}
          </ul>
        ) : null}
      </Modal>

      <Modal.Alert
        open={unmappedAlert !== null}
        onOpenChange={(v) => !v && setUnmappedAlert(null)}
        title="Unmapped accounts"
        body={unmappedAlert ? <UnmappedAccountsBody items={unmappedAlert} /> : null}
        onClose={() => setUnmappedAlert(null)}
      />
    </Page>
  )
}

function UnmappedAccountsBody({ items }: { items: string[] }) {
  const [expanded, setExpanded] = useState(false)
  const visible = expanded ? items : items.slice(0, 10)
  return (
    <>
      <p>
        {items.length} account{items.length === 1 ? '' : 's'} must be mapped before validating:
      </p>
      <ul className="mt-2 list-disc space-y-0.5 pl-5 text-sm">
        {visible.map((n, i) => (
          <li key={i}>{n}</li>
        ))}
      </ul>
      {items.length > 10 ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mt-1 h-auto px-1 py-0.5 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show less' : `…and ${items.length - 10} more (show all)`}
        </Button>
      ) : null}
      <p className="mt-3 text-sm">Use the combobox on each row or tap Auto-map, then try again.</p>
    </>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Step indicator
// ──────────────────────────────────────────────────────────────────────────

function StepIndicator({ current }: { current: Step }) {
  return (
    <div className="overflow-x-auto">
      <ol className="flex min-w-max items-center gap-2 sm:gap-3">
        {STEPS.map((s, i) => {
          const done = s.n < current
          const active = s.n === current
          return (
            <li key={s.n} className="flex items-center gap-2">
              <div
                className={cn(
                  'flex size-8 shrink-0 items-center justify-center rounded-pill text-xs font-semibold',
                  done && 'bg-success text-success-foreground',
                  active && 'bg-primary text-primary-foreground',
                  !done && !active && 'bg-muted text-muted-foreground',
                )}
              >
                {done ? <Check className="size-4" /> : s.n}
              </div>
              <span
                className={cn(
                  'whitespace-nowrap text-sm',
                  active ? 'font-semibold text-foreground' : 'text-muted-foreground',
                )}
              >
                {s.label}
              </span>
              {i < STEPS.length - 1 ? (
                <span className="h-px w-6 bg-border sm:w-10" aria-hidden />
              ) : null}
            </li>
          )
        })}
      </ol>
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Step 1 - Upload
// ──────────────────────────────────────────────────────────────────────────

function StepUpload({
  asOfDate,
  onAsOfDate,
  runUpload,
  upload,
  uploading,
  batchId,
  onIncludeSkipped,
  includingSkipped,
  onNext,
}: {
  asOfDate: string
  onAsOfDate: (v: string) => void
  onUploaded: (r: TbUploadResponse) => void
  runUpload: (file: File) => Promise<void>
  upload: TbUploadResponse | null
  uploading: boolean
  batchId: string | null
  onIncludeSkipped: (rows: TbSkippedRow[]) => Promise<void>
  includingSkipped: boolean
  onNext: () => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const [showAll, setShowAll] = useState(false)

  // Reset the "show all" toggle whenever a new file is uploaded.
  useEffect(() => {
    setShowAll(false)
  }, [upload?.batchId])

  // Lazy-fetch the full row list only when the user asks to expand.
  const allRowsQuery = useMappings(showAll ? batchId : null)

  const pick = () => inputRef.current?.click()

  const onFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    const file = files[0]
    if (!file) return
    await runUpload(file)
  }

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div className="grid grid-cols-1 gap-3 md:max-w-md">
          <FormField
            label="Trial balance date (as of)"
            id="tb-as-of-date"
            type="date"
            required
            value={asOfDate}
            onChange={(e) => onAsOfDate(e.currentTarget.value)}
            hint="The date this trial balance was generated, e.g. last day of the fiscal year."
          />
        </div>

        <ExpectedFormatExample />

        <div
          role="button"
          tabIndex={0}
          onClick={pick}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && pick()}
          onDragOver={(e) => {
            e.preventDefault()
            setDragOver(true)
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragOver(false)
            void onFiles(e.dataTransfer.files)
          }}
          className={cn(
            'flex flex-col items-center gap-2 rounded-md border-2 border-dashed p-8 text-center transition-colors cursor-pointer',
            dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50',
          )}
        >
          <FileSpreadsheet className="size-10 text-primary" aria-hidden />
          <p className="text-sm font-medium">Drop your trial balance file here or click to browse</p>
          <p className="text-xs text-muted-foreground">
            CSV or Excel (.xlsx, .xls, .xlsb, .xlsm)
          </p>
          <Input
            ref={inputRef}
            type="file"
            accept=".csv,.xlsx,.xls,.xlsb,.xlsm"
            className="hidden"
            onChange={(e) => void onFiles(e.currentTarget.files)}
          />
        </div>

        {uploading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Uploading & parsing…
          </div>
        ) : null}

        {upload ? (
          <>
            <Alert variant={upload.isBalanced ? 'default' : 'destructive'}>
              <AlertTitle>
                {upload.filename} · {upload.totalRows} accounts
              </AlertTitle>
              <AlertDescription className="space-y-1 text-sm">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span>Debits {fmtMoney(upload.totalDebits)}</span>
                  <span>Credits {fmtMoney(upload.totalCredits)}</span>
                  <Badge variant={upload.isBalanced ? 'success' : 'danger'}>
                    {upload.isBalanced ? 'Balanced' : 'NOT balanced'}
                  </Badge>
                  {upload.skippedRowCount && upload.skippedRowCount > 0 ? (
                    <span className="text-muted-foreground">
                      {upload.skippedRowCount} zero-balance account
                      {upload.skippedRowCount === 1 ? '' : 's'} were skipped - expand below
                      to include any.
                    </span>
                  ) : null}
                </div>
                {/* Why: users routinely confuse this badge with "Balance Sheet
                    will balance after import." It only confirms file-level
                    Debits = Credits. The post-import balance still depends on
                    correct mapping (e.g. a 1xxx Asset code mustn't carry a
                    credit balance) and on no other TB existing for the same
                    as-of date. Spell that out here so the user understands the
                    Verify step's NOT BALANCED is a real follow-up, not a bug. */}
                <p className="text-xs text-muted-foreground">
                  This only checks that the file&rsquo;s debit total matches its
                  credit total. The Verify step rebuilds the balance sheet from the
                  ledger after mapping - if accounts are mapped to the wrong section
                  or another TB for {asOfDate || 'this date'} is already imported,
                  Verify can still come back NOT balanced.
                </p>
              </AlertDescription>
            </Alert>

            {upload.skippedRows && upload.skippedRows.length > 0 ? (
              <SkippedRowsPanel
                rows={upload.skippedRows}
                onInclude={onIncludeSkipped}
                including={includingSkipped}
              />
            ) : null}

            {upload.totalRows === 0 && upload.detection ? (
              // Why: a zero-row import is almost always a column-
              // detection failure. Show what the parser actually
              // decided so the user can spot the mismatch immediately
              // (e.g. "we thought your debit was column A - that's
              // your account name") and fix the file or report it.
              <Alert variant="destructive">
                <AlertTitle>No accounts imported - column detection looks wrong</AlertTitle>
                <AlertDescription className="space-y-2 text-sm">
                  <div>
                    Header row:{' '}
                    <strong>
                      {upload.detection.headerRow >= 0
                        ? `row ${upload.detection.headerRow + 1}`
                        : 'none detected'}
                    </strong>
                    {' · '}Account column:{' '}
                    <strong>
                      {colLetter(
                        upload.detection.colMap.accountCode ??
                          upload.detection.colMap.accountName,
                      )}
                    </strong>
                    {upload.detection.colMap.debit !== undefined ? (
                      <>
                        {' · '}Debit column:{' '}
                        <strong>{colLetter(upload.detection.colMap.debit)}</strong>
                      </>
                    ) : null}
                    {upload.detection.colMap.credit !== undefined ? (
                      <>
                        {' · '}Credit column:{' '}
                        <strong>{colLetter(upload.detection.colMap.credit)}</strong>
                      </>
                    ) : null}
                    {upload.detection.colMap.balance !== undefined ? (
                      <>
                        {' · '}Balance column:{' '}
                        <strong>{colLetter(upload.detection.colMap.balance)}</strong>
                      </>
                    ) : null}
                    .
                  </div>
                  {upload.detection.firstSkipReasons &&
                  upload.detection.firstSkipReasons.length > 0 ? (
                    <div>
                      Skip reasons:{' '}
                      <span className="text-muted-foreground">
                        {upload.detection.firstSkipReasons.join(' · ')}
                      </span>
                    </div>
                  ) : null}
                  {upload.detection.sampleDataRows &&
                  upload.detection.sampleDataRows.length > 0 ? (
                    <div className="space-y-1">
                      <div className="text-xs font-semibold">
                        First data rows (as the parser saw them):
                      </div>
                      <div className="overflow-x-auto rounded border border-destructive/40 bg-card">
                        <table className="w-full text-[11px]">
                          <thead className="bg-muted/50 text-left">
                            <tr>
                              <th className="px-2 py-1">Row</th>
                              {upload.detection.sampleDataRows[0]!.cells.map((_c, i) => (
                                <th key={i} className="px-2 py-1">
                                  Col {colLetter(i)}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {upload.detection.sampleDataRows.map((r) => (
                              <tr key={r.rowIndex} className="border-t border-border">
                                <td className="px-2 py-1 font-mono">{r.rowIndex}</td>
                                {r.cells.map((c, i) => (
                                  <td key={i} className="px-2 py-1 font-mono">
                                    {c}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        ∅ = empty cell. If your debit/credit values land
                        in different columns than what the parser
                        chose, the source file may have hidden columns
                        or merged cells offsetting the amounts.
                      </div>
                    </div>
                  ) : null}
                  <div className="text-xs text-muted-foreground">
                    Tip: open the file format example above for a
                    layout that always works.
                  </div>
                </AlertDescription>
              </Alert>
            ) : null}

            {(() => {
              // The preview table can show either the first 20 rows from the
              // upload response or the full set fetched on demand. We map the
              // mapping-row shape (snake_case) into the preview-row shape so
              // the columns below stay identical.
              const fullRows: TbPreviewRow[] = (allRowsQuery.data?.rows ?? []).map(
                (r, idx) => ({
                  rowNumber: idx + 1,
                  externalAccountCode: r.external_account_code ?? '',
                  externalAccountName: r.external_account_name ?? '',
                  debit: parseFloat(String(r.debit ?? 0)) || 0,
                  credit: parseFloat(String(r.credit ?? 0)) || 0,
                }),
              )
              const visible = showAll && fullRows.length > 0 ? fullRows : upload.preview
              const remaining = upload.totalRows - upload.preview.length
              return (
                <>
                  <DataTable
                    sortable={false}
                    rows={visible}
                    columns={[
                      {
                        header: () => <span className="block text-center">#</span>,
                        accessorKey: 'rowNumber',
                        cell: ({ row }) => (
                          <span className="block text-center text-xs text-muted-foreground">
                            {row.original.rowNumber}
                          </span>
                        ),
                      },
                      {
                        header: 'Account code',
                        accessorKey: 'externalAccountCode',
                        cell: ({ row }) => (
                          <span className="font-mono text-xs">
                            {row.original.externalAccountCode}
                          </span>
                        ),
                      },
                      {
                        header: 'Account name',
                        accessorKey: 'externalAccountName',
                      },
                      {
                        header: () => <span className="block text-right">Debit</span>,
                        accessorKey: 'debit',
                        cell: ({ row }) => (
                          <span className="block text-right font-mono text-xs">
                            {row.original.debit > 0 ? fmtMoney(row.original.debit) : ''}
                          </span>
                        ),
                      },
                      {
                        header: () => <span className="block text-right">Credit</span>,
                        accessorKey: 'credit',
                        cell: ({ row }) => (
                          <span className="block text-right font-mono text-xs">
                            {row.original.credit > 0 ? fmtMoney(row.original.credit) : ''}
                          </span>
                        ),
                      },
                    ]}
                  />
                  {remaining > 0 ? (
                    <div className="flex justify-center">
                      {showAll && allRowsQuery.isLoading ? (
                        <p className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Loader2 className="size-3 animate-spin" /> Loading all rows…
                        </p>
                      ) : (
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          className="h-auto px-1 py-0.5 text-xs"
                          onClick={() => setShowAll((v) => !v)}
                        >
                          {showAll
                            ? `Show only the first ${upload.preview.length}`
                            : `…and ${remaining} more rows (show all)`}
                        </Button>
                      )}
                    </div>
                  ) : null}
                </>
              )
            })()}

            <Toolbar align="end">
              <Button onClick={onNext}>Next: map accounts</Button>
            </Toolbar>
          </>
        ) : null}
      </CardContent>
    </Card>
  )
}

/**
 * Expandable panel listing every row the parser filtered out (banner /
 * header / totals / "both sides populated"). The user can review them,
 * pick any that were dropped by mistake, and click "Include selected"
 * to add them into the batch. Paginated 30 per page so a 200-row export
 * is still navigable.
 */
const SKIPPED_PAGE_SIZE = 30

function SkippedRowsPanel({
  rows,
  onInclude,
  including,
}: {
  rows: TbSkippedRow[]
  onInclude: (rows: TbSkippedRow[]) => Promise<void>
  including: boolean
}) {
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [page, setPage] = useState(1)

  const totalPages = Math.max(1, Math.ceil(rows.length / SKIPPED_PAGE_SIZE))

  // If the list shrinks (after a successful include) and the current
  // page is now past the end, snap back into range.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages)
  }, [page, totalPages])

  const pageRows = useMemo(
    () => rows.slice((page - 1) * SKIPPED_PAGE_SIZE, page * SKIPPED_PAGE_SIZE),
    [rows, page],
  )

  const allOnPageSelected =
    pageRows.length > 0 && pageRows.every((r) => selected.has(r.rowIndex))
  const allSelected = rows.length > 0 && selected.size === rows.length

  const togglePage = () => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (allOnPageSelected) {
        for (const r of pageRows) next.delete(r.rowIndex)
      } else {
        for (const r of pageRows) next.add(r.rowIndex)
      }
      return next
    })
  }

  const toggleRow = (rowIndex: number) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(rowIndex)) next.delete(rowIndex)
      else next.add(rowIndex)
      return next
    })
  }

  const toggleAll = () => {
    setSelected((prev) =>
      prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.rowIndex)),
    )
  }

  const handleInclude = async () => {
    const picked = rows.filter((r) => selected.has(r.rowIndex))
    if (picked.length === 0) return
    await onInclude(picked)
    setSelected(new Set())
  }

  const fromIndex = (page - 1) * SKIPPED_PAGE_SIZE + 1
  const toIndex = Math.min(page * SKIPPED_PAGE_SIZE, rows.length)

  return (
    <div className="rounded-md border border-border bg-muted/30">
      <Button
        type="button"
        variant="ghost"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-start gap-2 rounded-md px-3 py-2 text-left text-sm font-medium hover:bg-muted/60"
      >
        {open ? (
          <ChevronDown className="size-4" aria-hidden />
        ) : (
          <ChevronRight className="size-4" aria-hidden />
        )}
        <span>
          {rows.length} zero-balance account{rows.length === 1 ? '' : 's'} - review &amp;
          include any you want imported
        </span>
      </Button>

      {open ? (
        <div className="space-y-3 border-t border-border px-3 py-3">
          <Toolbar align="between">
            <div className="flex items-center gap-3 text-sm">
              <Checkbox
                checked={allSelected}
                onCheckedChange={toggleAll}
                aria-label="Select all skipped rows"
              />
              <span className="text-muted-foreground">
                {selected.size} of {rows.length} selected
              </span>
            </div>
            <Button
              type="button"
              size="sm"
              onClick={() => void handleInclude()}
              disabled={selected.size === 0 || including}
            >
              {including ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Plus className="size-4" />
              )}
              Include selected
            </Button>
          </Toolbar>

          <div className="overflow-x-auto rounded-md border bg-card">
            <div className="grid min-w-[760px] grid-cols-[40px_60px_1fr_120px_1fr_110px_110px] items-center gap-2 border-b bg-muted/40 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              <div className="flex items-center justify-center">
                <Checkbox
                  checked={allOnPageSelected}
                  onCheckedChange={togglePage}
                  aria-label="Select all on this page"
                />
              </div>
              <div>Row</div>
              <div>Reason</div>
              <div>Code</div>
              <div>Name</div>
              <div className="text-right">Debit</div>
              <div className="text-right">Credit</div>
            </div>
            {pageRows.map((r) => {
              const checked = selected.has(r.rowIndex)
              return (
                <div
                  key={r.rowIndex}
                  className={cn(
                    'grid min-w-[760px] grid-cols-[40px_60px_1fr_120px_1fr_110px_110px] items-center gap-2 border-b px-3 py-2 text-sm last:border-b-0',
                    checked && 'bg-primary/5',
                  )}
                >
                  <div className="flex items-center justify-center">
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggleRow(r.rowIndex)}
                      aria-label={`Select row ${r.rowIndex}`}
                    />
                  </div>
                  <div className="font-mono text-xs text-muted-foreground">{r.rowIndex}</div>
                  <div className="truncate text-xs text-muted-foreground" title={r.reason}>
                    {r.reason}
                  </div>
                  <div className="truncate font-mono text-xs">
                    {r.externalAccountCode || '-'}
                  </div>
                  <div className="truncate" title={r.externalAccountName}>
                    {r.externalAccountName || '-'}
                  </div>
                  <div className="text-right font-mono text-xs">
                    {r.debit > 0 ? fmtMoney(r.debit) : ''}
                  </div>
                  <div className="text-right font-mono text-xs">
                    {r.credit > 0 ? fmtMoney(r.credit) : ''}
                  </div>
                </div>
              )
            })}
          </div>

          {totalPages > 1 ? (
            <Toolbar align="between">
              <span className="text-xs text-muted-foreground">
                Showing {fromIndex}–{toIndex} of {rows.length}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="size-4" /> Prev
                </Button>
                <span className="text-xs text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next <ChevronRight className="size-4" />
                </Button>
              </div>
            </Toolbar>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/**
 * Collapsible "what should my file look like?" example shown above the
 * dropzone. We don't gate the upload on this - the parser is forgiving
 * - but having a visual reference next to the upload control cuts down
 * on bad files reaching the mapping step. Kept as plain inline tables
 * (not real DataTables) so the structure of the example matches the
 * spreadsheet structure the user is reading.
 */
function ExpectedFormatExample() {
  const [open, setOpen] = useState(false)
  return (
    <div className="rounded-md border border-border bg-muted/30">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium hover:bg-muted/60"
        aria-expanded={open}
      >
        {open ? <ChevronDown className="size-4" aria-hidden /> : <ChevronRight className="size-4" aria-hidden />}
        <Info className="size-4 text-primary" aria-hidden />
        <span>What should my trial balance file look like?</span>
      </button>
      {open ? (
        <div className="space-y-4 border-t border-border px-3 py-3 text-xs">
          <p className="text-muted-foreground">
            Upload a CSV or Excel (.xlsx / .xls) export of your trial balance.
            We auto-detect either layout below, plus the QuickBooks
            "Parent:Child" account format (e.g.{' '}
            <code className="rounded bg-muted px-1">1000 Cash:1001 BofA</code>).
          </p>

          <div>
            <p className="mb-1 font-semibold">
              Recommended - three columns: Account, Debit, Credit
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-border bg-card">
                <thead className="bg-muted/60">
                  <tr>
                    <th className="border border-border px-2 py-1 text-left">Account</th>
                    <th className="border border-border px-2 py-1 text-right">Debit</th>
                    <th className="border border-border px-2 py-1 text-right">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border px-2 py-1">1000 Cash:1001 BofA</td>
                    <td className="border border-border px-2 py-1 text-right">178,405.86</td>
                    <td className="border border-border px-2 py-1 text-right"></td>
                  </tr>
                  <tr>
                    <td className="border border-border px-2 py-1">1000 Cash:1004 Morgan Stanley</td>
                    <td className="border border-border px-2 py-1 text-right"></td>
                    <td className="border border-border px-2 py-1 text-right">28,211.23</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-2 py-1">20000 Accounts Payable</td>
                    <td className="border border-border px-2 py-1 text-right"></td>
                    <td className="border border-border px-2 py-1 text-right">3,805.95</td>
                  </tr>
                  <tr className="bg-muted/40 font-semibold">
                    <td className="border border-border px-2 py-1">TOTAL</td>
                    <td className="border border-border px-2 py-1 text-right">178,405.86</td>
                    <td className="border border-border px-2 py-1 text-right">32,017.18</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <p className="mb-1 font-semibold">
              Also accepted - two columns: Account, Balance (signed)
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-border bg-card">
                <thead className="bg-muted/60">
                  <tr>
                    <th className="border border-border px-2 py-1 text-left">Account</th>
                    <th className="border border-border px-2 py-1 text-right">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border px-2 py-1">1001 Cash BofA</td>
                    <td className="border border-border px-2 py-1 text-right">178,405.86</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-2 py-1">1004 Cash Morgan Stanley</td>
                    <td className="border border-border px-2 py-1 text-right">(28,211.23)</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Negatives can be either parenthesised (e.g.{' '}
              <code className="rounded bg-muted px-1">(28,211.23)</code>) or
              prefixed with a minus sign. Positive = debit, negative = credit.
            </p>
          </div>

          <div>
            <p className="mb-1 font-semibold">Tips</p>
            <ul className="list-disc space-y-0.5 pl-4 text-muted-foreground">
              <li>
                Banner rows (company name, "Trial Balance", "As of …",
                blank rows) are auto-skipped.
              </li>
              <li>
                The "TOTAL" row at the end (where Debit and Credit are
                equal) marks the end of the data - anything below is
                ignored.
              </li>
              <li>
                Currency symbols ($, €, £, ¥) and thousand separators
                are handled automatically.
              </li>
              <li>
                Accounts with zero balance are tracked in diagnostics
                but not imported, since they have nothing to post.
              </li>
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Step 2 - Map
// ──────────────────────────────────────────────────────────────────────────

function StepMap({
  rows,
  loading,
  options,
  drafts,
  highlightUnmapped,
  onChange,
  onAutoMap,
  autoMapping,
  onBack,
  onSave,
  saving,
}: {
  rows: TbMappingRow[]
  loading: boolean
  options: ComboboxOption<string>[]
  drafts: Record<number, string>
  highlightUnmapped: boolean
  onChange: (rowId: number, value: string | null) => void
  onAutoMap: () => void
  autoMapping: boolean
  onBack: () => void
  onSave: () => void
  saving: boolean
}) {
  const mappedCount = rows.filter((r) => !!drafts[r.id]).length

  if (loading) {
    return (
      <Card>
        <CardContent className="py-10">
          <LoadingSpinner />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <p className="text-sm text-muted-foreground">
          {mappedCount} of {rows.length} mapped
        </p>

        {/* md+: grid */}
        <div className="hidden overflow-x-auto md:block">
          <div className="min-w-[900px] rounded-md border">
            <div className="grid grid-cols-[140px_1fr_110px_110px_minmax(260px,1.5fr)_110px] items-center gap-2 border-b bg-muted/40 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              <div>Ext. code</div>
              <div>Ext. name</div>
              <div className="text-right">Debit</div>
              <div className="text-right">Credit</div>
              <div>Map to</div>
              <div>Status</div>
            </div>
            {rows.map((row) => {
              const isUnmapped = !drafts[row.id]
              return (
                <div
                  key={row.id}
                  className={cn(
                    'grid grid-cols-[140px_1fr_110px_110px_minmax(260px,1.5fr)_110px] items-center gap-2 border-b px-3 py-2 last:border-b-0',
                    highlightUnmapped && isUnmapped && 'bg-warning/20',
                  )}
                >
                  <div className="font-mono text-xs">{row.external_account_code || '-'}</div>
                  <div className="truncate">{row.external_account_name || '-'}</div>
                  <div className="text-right font-mono text-xs">
                    {parseFloat(String(row.debit ?? 0)) > 0 ? fmtMoney(row.debit) : ''}
                  </div>
                  <div className="text-right font-mono text-xs">
                    {parseFloat(String(row.credit ?? 0)) > 0 ? fmtMoney(row.credit) : ''}
                  </div>
                  <Combobox
                    options={options}
                    value={drafts[row.id] ?? null}
                    placeholder="Search account…"
                    onChange={(v) => onChange(row.id, v)}
                  />
                  <div>
                    <Badge variant={isUnmapped ? 'warning' : 'success'}>
                      {isUnmapped ? 'Unmapped' : 'Mapped'}
                    </Badge>
                  </div>
                </div>
              )
            })}
            {rows.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                No rows to map.
              </div>
            ) : null}
          </div>
        </div>

        {/* xs/sm: cards */}
        <div className="space-y-3 md:hidden">
          {rows.map((row) => {
            const isUnmapped = !drafts[row.id]
            return (
              <div
                key={row.id}
                className={cn(
                  'space-y-2 rounded-md border bg-card p-3',
                  highlightUnmapped && isUnmapped && 'border-warning',
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">
                      {row.external_account_name || '-'}
                    </p>
                    <p className="font-mono text-xs text-muted-foreground">
                      {row.external_account_code || '-'}
                    </p>
                  </div>
                  <Badge variant={isUnmapped ? 'warning' : 'success'}>
                    {isUnmapped ? 'Unmapped' : 'Mapped'}
                  </Badge>
                </div>
                <div className="flex gap-3 text-xs text-muted-foreground">
                  {parseFloat(String(row.debit ?? 0)) > 0 ? (
                    <span>Debit {fmtMoney(row.debit)}</span>
                  ) : null}
                  {parseFloat(String(row.credit ?? 0)) > 0 ? (
                    <span>Credit {fmtMoney(row.credit)}</span>
                  ) : null}
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Map to</label>
                  <Combobox
                    options={options}
                    value={drafts[row.id] ?? null}
                    placeholder="Search account…"
                    onChange={(v) => onChange(row.id, v)}
                  />
                </div>
              </div>
            )
          })}
        </div>

        {/* Why: keeping the action toolbar at the bottom matches StepUpload,
            StepValidate, StepImporting, and StepVerify so navigation feels
            consistent across the wizard - left-aligned utilities (Auto-map),
            right-aligned navigation (Back / Save & validate). */}
        <Toolbar align="between">
          <Button variant="secondary" onClick={onAutoMap} disabled={autoMapping}>
            {autoMapping ? <Loader2 className="size-4 animate-spin" /> : null} Auto-map
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onBack}>
              <ArrowLeft className="size-4" /> Back
            </Button>
            <Button onClick={onSave} disabled={saving}>
              {saving ? <Loader2 className="size-4 animate-spin" /> : null} Save & validate
            </Button>
          </div>
        </Toolbar>
      </CardContent>
    </Card>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Step 3 - Validate
// ──────────────────────────────────────────────────────────────────────────

function StepValidate({
  result,
  onBack,
  onImport,
  importing,
}: {
  result: TbValidationResponse | null
  onBack: () => void
  onImport: () => void
  importing: boolean
}) {
  // Why: these specific warning patterns almost always cause Verify to come
  // back NOT BALANCED - duplicate target codes collapse multiple source rows
  // into one, asset accounts on the credit side flip Total Assets negative,
  // equity accounts on the debit side break the equity total. We surface them
  // as a stronger callout than the generic "Warnings" list so users don't sail
  // past with a green check and discover the imbalance only at Verify.
  const warnings = result?.warnings ?? []
  const balanceBreakingWarnings = warnings.filter((w) =>
    /duplicate mapped account|asset .*credit balance|equity .*debit balance|liability .*debit balance/i.test(w),
  )
  const hasBalanceBreaking = balanceBreakingWarnings.length > 0

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        {result?.errors && result.errors.length > 0 ? (
          <Alert variant="destructive">
            <AlertTitle>Errors - must fix</AlertTitle>
            <AlertDescription>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm">
                {result.errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        ) : null}

        {hasBalanceBreaking ? (
          <Alert variant="destructive">
            <AlertTitle>These warnings usually break the balance sheet</AlertTitle>
            <AlertDescription className="space-y-2">
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm">
                {balanceBreakingWarnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
              <p className="text-sm">
                Importing as-is is allowed, but the Verify step will likely come
                back NOT balanced. Click &ldquo;Back to mappings&rdquo; to fix
                them first if you can.
              </p>
            </AlertDescription>
          </Alert>
        ) : null}

        {warnings.length > 0 && warnings.length > balanceBreakingWarnings.length ? (
          <Alert>
            <AlertTitle>Warnings</AlertTitle>
            <AlertDescription>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm">
                {warnings
                  .filter((w) => !balanceBreakingWarnings.includes(w))
                  .map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
              </ul>
            </AlertDescription>
          </Alert>
        ) : null}

        {result?.valid ? (
          <Alert>
            <AlertTitle>
              {hasBalanceBreaking ? 'Validation passed (with warnings)' : 'Validation passed'}
            </AlertTitle>
            <AlertDescription>
              {hasBalanceBreaking
                ? 'You can still import, but expect the Verify balance check to flag an imbalance until the warnings above are resolved.'
                : 'Ready to import.'}
            </AlertDescription>
          </Alert>
        ) : null}

        <Toolbar align="between">
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="size-4" /> Back to mappings
          </Button>
          <Button onClick={onImport} disabled={!result?.valid || importing}>
            {importing ? <Loader2 className="size-4 animate-spin" /> : <UploadIcon className="size-4" />}
            Import trial balance
          </Button>
        </Toolbar>
      </CardContent>
    </Card>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Step 4 - Importing
// ──────────────────────────────────────────────────────────────────────────

function StepImporting({ error, onBack }: { error: string | null; onBack: () => void }) {
  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        {error ? (
          <>
            <Alert variant="destructive">
              <AlertTitle>Import failed</AlertTitle>
              <AlertDescription>
                <p>{error}</p>
                <p className="mt-2 text-sm">Possible causes:</p>
                <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm">
                  <li>The accounting period may be closed or locked.</li>
                  <li>A previous TB import may conflict with this date.</li>
                  <li>Some accounts may not be properly mapped.</li>
                </ul>
              </AlertDescription>
            </Alert>
            <Toolbar align="end">
              <Button variant="secondary" onClick={onBack}>
                Back to validate
              </Button>
            </Toolbar>
          </>
        ) : (
          <div className="flex items-center gap-2 text-sm">
            <Loader2 className="size-4 animate-spin" /> Importing, please wait…
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Step 5 - Verify
// ──────────────────────────────────────────────────────────────────────────

function StepVerify({
  batchId,
  summary,
  period,
  onBackToMap,
  onReset,
}: {
  batchId: string | null
  summary: { rows: number; asOf: string } | null
  period: BalanceSheetPeriod | null
  onBackToMap: () => void
  onReset: () => void
}) {
  // Assets-negative and "not balanced" are the two triggers for the
  // diagnostic panel - both point at the same likely root causes (bad
  // mapping or a stacked duplicate import), so we pull diagnostics for
  // either condition.
  const assetsNegative = !!period && period.totals.totalAssets < 0
  const needsDiagnostics = !!period && (!period.isBalanced || assetsNegative)
  const diagnosticsQuery = useTbDiagnostics(batchId, needsDiagnostics)

  const imbalance = period
    ? period.totals.totalAssets - period.totals.totalLiabilities - period.totals.totalEquity
    : 0

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        {summary ? (
          <Alert>
            <AlertTitle>Import successful</AlertTitle>
            <AlertDescription>
              {summary.rows} accounts imported as of {summary.asOf}.
            </AlertDescription>
          </Alert>
        ) : null}

        {period ? (
          <div className="overflow-hidden rounded-md border">
            <div className="grid grid-cols-[1fr_auto] gap-x-4 border-b bg-muted/40 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              <div>Section</div>
              <div className="text-right">Amount</div>
            </div>
            <VerifyRow label="Total assets" value={fmtMoney(period.totals.totalAssets)} />
            <VerifyRow label="Total liabilities" value={fmtMoney(period.totals.totalLiabilities)} />
            <VerifyRow label="Total equity" value={fmtMoney(period.totals.totalEquity)} />
            <VerifyRow label="Net income" value={fmtMoney(period.totals.netIncome)} />
            <VerifyRow
              label="Retained earnings"
              value={fmtMoney(period.totals.retainedEarnings)}
            />
            <div className="grid grid-cols-[1fr_auto] items-center gap-x-4 border-t-2 bg-muted/30 px-3 py-2 text-sm">
              <div className="font-semibold">Balanced?</div>
              <div className="text-right">
                <Badge variant={period.isBalanced ? 'success' : 'danger'}>
                  {period.isBalanced ? 'YES' : 'NO'}
                </Badge>
              </div>
            </div>
          </div>
        ) : null}

        {needsDiagnostics ? (
          <VerifyDiagnostics
            assetsNegative={assetsNegative}
            imbalance={imbalance}
            loading={diagnosticsQuery.isLoading}
            overdraftAccounts={diagnosticsQuery.data?.assetOverdraftAccounts ?? []}
            duplicateBatches={diagnosticsQuery.data?.duplicateBatches ?? []}
            onBackToMap={onBackToMap}
          />
        ) : null}

        <Toolbar align="end">
          <Button variant="secondary" onClick={onReset}>
            Import another
          </Button>
          <Button asChild>
            <Link to="/reporting">View reports</Link>
          </Button>
        </Toolbar>
      </CardContent>
    </Card>
  )
}

/**
 * Diagnostic panel shown under the Verify totals when the balance check
 * fails. Surfaces the two most common root causes (credit balances in 1xxx
 * accounts, duplicate batches for the same date) so the user can decide
 * whether to go back to Map accounts or keep the ledger as-is.
 */
function VerifyDiagnostics({
  assetsNegative,
  imbalance,
  loading,
  overdraftAccounts,
  duplicateBatches,
  onBackToMap,
}: {
  assetsNegative: boolean
  imbalance: number
  loading: boolean
  overdraftAccounts: NonNullable<
    ReturnType<typeof useTbDiagnostics>['data']
  >['assetOverdraftAccounts']
  duplicateBatches: TbDiagnosticsBatch[]
  onBackToMap: () => void
}) {
  return (
    <Alert variant="destructive" className="space-y-3">
      <AlertTitle>
        Balance sheet is off by {fmtMoney(Math.abs(imbalance))}
        {assetsNegative ? ' - Total Assets is negative' : ''}
      </AlertTitle>
      <AlertDescription className="space-y-3">
        <p>
          This usually means one of two things. Check these before deciding whether to re-map or
          accept the imbalance.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-sm">
            <Loader2 className="size-4 animate-spin" /> Looking up the likely causes…
          </div>
        ) : (
          <>
            {duplicateBatches.length > 0 ? (
              <div className="rounded-md border border-destructive/40 bg-background/60 p-3 text-sm">
                <div className="font-semibold">
                  Another TB for this date is still in the ledger
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Both batches&rsquo; entries are summed into the Verify balance sheet. Delete or
                  reverse the older batch to clear the imbalance.
                </p>
                <ul className="mt-2 space-y-1">
                  {duplicateBatches.map((b) => (
                    <li key={b.id} className="flex flex-wrap items-center gap-x-3 text-xs">
                      <span className="font-medium">{b.filename || `Batch ${b.id}`}</span>
                      <span className="text-muted-foreground">
                        {b.importedRows ?? 0} rows · {new Date(b.createdAt).toLocaleDateString()}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {overdraftAccounts.length > 0 ? (
              <div className="rounded-md border border-destructive/40 bg-background/60 p-3 text-sm">
                <div className="font-semibold">
                  These Asset (1xxx) accounts have a net CREDIT balance
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Assets should normally be debit. A credit balance here usually means a liability
                  or equity line was mapped to a 1xxx code by mistake, or a parent row from
                  QuickBooks was mapped alongside its children.
                </p>
                <div className="mt-2 overflow-hidden rounded-md border">
                  <div className="grid grid-cols-[auto_1fr_auto] gap-x-3 border-b bg-muted/40 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    <div>Code</div>
                    <div>Subcategory</div>
                    <div className="text-right">Credit balance</div>
                  </div>
                  {overdraftAccounts.map((a) => (
                    <div
                      key={a.account_code}
                      className="grid grid-cols-[auto_1fr_auto] items-center gap-x-3 border-b px-2 py-1 text-xs last:border-b-0"
                    >
                      <div className="font-mono">{a.account_code}</div>
                      <div className="truncate">{a.subcategory || '-'}</div>
                      <div className="text-right font-mono">{fmtMoney(Math.abs(a.balance))}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {overdraftAccounts.length === 0 && duplicateBatches.length === 0 ? (
              <p className="text-sm">
                No 1xxx accounts with credit balances and no duplicate batches found - the
                imbalance is coming from elsewhere (likely a skipped row during mapping or a
                mapping that put a BS account onto the Income Statement). Go back to Map accounts
                to review.
              </p>
            ) : null}
          </>
        )}

        <Toolbar align="end">
          <Button variant="secondary" onClick={onBackToMap}>
            <ArrowLeft className="size-4" /> Back to Map accounts
          </Button>
        </Toolbar>
      </AlertDescription>
    </Alert>
  )
}

function VerifyRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[1fr_auto] items-center gap-x-4 border-b px-3 py-2 text-sm last:border-b-0">
      <div>{label}</div>
      <div className="text-right font-mono text-xs">{value}</div>
    </div>
  )
}
